import { css } from '@emotion/react';
import { DateTime } from 'luxon';
import { MouseEvent, ReactElement, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { SasIconButton } from '../SasIcon';
import { theme } from '../theme';

import { SortSelect } from './SortSelect';

const styles = {
  wrapper: css`
    position: relative;
  `,
  tabsSlider: css`
    overflow-x: scroll;
    overflow-y: hidden;
    scroll-behavior: smooth;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `,
  tabs: css`
    list-style: none;
    display: flex;
    align-items: flex-end;
    gap: ${theme.spacing.xs};
    margin: 0;
    padding: 0;
  `,
  tabCommon: css`
    display: flex;
    flex-direction: column;
    font-size: ${theme.fontSize.xs};
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
  `,
  tabActive: css`
    height: 48px;
    padding: 4px 16px;
    background: ${theme.colors.blue40};
    color: ${theme.colors.white};
    cursor: default;
    border-top-left-radius: ${theme.borders.radius.sm};
    border-top-right-radius: ${theme.borders.radius.sm};

    @media (min-width: ${theme.breakpoints.mobileL}) {
      height: 52px;
      padding: 8px 16px;
    }
  `,
  tab: (isSelectable: boolean) => css`
    height: 40px;
    background: ${theme.colors.turquoise80};
    color: ${theme.colors.blue30};
    cursor: ${isSelectable ? 'pointer' : 'default'};
    border-radius: 0;
    opacity: ${isSelectable ? 1 : 0.5};
    padding: 4px 16px;
    border-top-left-radius: ${theme.borders.radius.sm};

    &:hover {
      background: ${isSelectable ? theme.colors.turquoise70 : theme.colors.turquoise80};
    }

    @media (min-width: ${theme.breakpoints.mobileL}) {
      height: 44px;
    }
  `,
  tabLabel: css`
    font-weight: ${theme.fontWeight.bold};
    font-size: ${theme.fontSize.sm};
  `,
  bar: (showSlider: boolean) => css`
    height: 44px;
    background: ${theme.colors.blue40};
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${theme.spacing.md};
    color: ${theme.colors.white};
    border-top-right-radius: ${showSlider ? 0 : theme.borders.radius.md};

    @media (min-width: ${theme.breakpoints.mobileL}) {
      height: 56px;
    }
  `,
  title: css`
    font-size: ${theme.fontSize.sm};
    font-weight: ${theme.fontWeight.black};
    text-transform: uppercase;
    margin: 0;

    @media (min-width: ${theme.breakpoints.mobileL}) {
      font-size: ${theme.fontSize.lg};
    }
  `,
  navigationButton: css`
    all: unset;
    position: absolute;
    background-color: ${theme.colors.white};
    border-radius: 50%;
    width: 32px;
    height: 32px;
    top: 0;

    &:hover,
    &:focus {
      background-color: ${theme.colors.white};
    }

    @media (min-width: ${theme.breakpoints.tablet}) {
      display: none;
    }
  `,
  leftNavigationButton: css`
    left: -8px;
  `,
  rightNavigationButton: css`
    right: -8px;
  `,
  overlay: css`
    position: absolute;
    top: 0;
    height: 48px;
    width: 80px;
    pointer-events: none;
  `,
  leftOverlay: css`
    left: 0;
    background: linear-gradient(to left, rgba(245, 245, 245, 0) 5%, rgba(245, 245, 245, 1));
  `,
  rightOverlay: css`
    right: 0;
    background: linear-gradient(to right, rgba(245, 245, 245, 0) 5%, rgba(245, 245, 245, 1));
  `,
};

export type Tab = {
  date: DateTime;
  label?: string | ReactElement;
  isActive?: boolean;
  isSelectable?: boolean;
};

type Props = {
  title: string;
  tabs: Tab[] | undefined;
  hidePrice?: boolean;
  sortOptions?: { label: string; value: string }[];
  onDateChange?: (date: DateTime) => void;
  onSortChange?: (option: string) => void;
};

export function FlightTabs({ title, tabs, onDateChange, sortOptions, onSortChange }: Props) {
  const [showSlider, setShowSlider] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const sliderRef = useRef<HTMLUListElement>(null);
  const activeTabRef = useRef<HTMLLIElement>(null);

  const tabsOverflowing = tabs && tabs?.length > 3;

  useEffect(() => {
    if (tabsOverflowing) {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  useEffect(() => {
    // +32 is to cover the content padding
    if (tabsOverflowing && sliderRef?.current && sliderRef.current.scrollWidth + 32 > windowWidth) {
      setShowSlider(true);
    } else {
      setShowSlider(false);
    }
  }, [sliderRef, tabsOverflowing, windowWidth]);

  useEffect(() => {
    if (showSlider && activeTabRef?.current) {
      activeTabRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
      handleScrollArrows();
    }
  }, [activeTabRef, showSlider]);

  const handleScrollArrows = () => {
    if (sliderRef?.current) {
      const scrollLeft = sliderRef.current.scrollLeft;
      const maxScrollLeft = sliderRef.current.scrollWidth - sliderRef.current.clientWidth;

      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < maxScrollLeft);
    }
  };

  const handleTabClick = (event: MouseEvent<HTMLLIElement>, isSelectable: boolean, date: DateTime) => {
    if (isSelectable) {
      event.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      onDateChange?.(date);
    }
  };

  return (
    <div css={showSlider ? styles.wrapper : undefined}>
      <ul css={[styles.tabs, showSlider ? styles.tabsSlider : undefined]} ref={sliderRef} onScroll={handleScrollArrows}>
        {tabs?.map(({ date, isSelectable, isActive, label }) => (
          <li
            key={date.toString()}
            css={[styles.tabCommon, isActive ? styles.tabActive : styles.tab(!!isSelectable)]}
            onClick={event => handleTabClick(event, !!isSelectable, date)}
            ref={isActive ? activeTabRef : undefined}
          >
            <div>{date.toFormat('ccc dd LLL')}</div>
            {!!label && <div css={styles.tabLabel}>{label}</div>}
          </li>
        ))}
      </ul>
      <div css={styles.bar(!!showSlider)}>
        <p css={styles.title}>{title}</p>
        {sortOptions && sortOptions.length > 0 && onSortChange && (
          <div>
            <SortSelect sortOptions={sortOptions} onSortChange={onSortChange} />
          </div>
        )}
      </div>
      {showSlider && showLeftArrow && (
        <>
          <div css={[styles.overlay, styles.leftOverlay]} />
          <SasIconButton
            name="in--arrow-left"
            css={[styles.navigationButton, styles.leftNavigationButton]}
            color={theme.colors.gray40}
            onClick={() => sliderRef?.current?.scrollBy({ left: -150, behavior: 'smooth' })}
          />
        </>
      )}
      {showSlider && showRightArrow && (
        <>
          <div css={[styles.overlay, styles.rightOverlay]} />
          <SasIconButton
            name="in--arrow-right"
            css={[styles.navigationButton, styles.rightNavigationButton]}
            color={theme.colors.gray40}
            onClick={() => sliderRef?.current?.scrollBy({ left: 150, behavior: 'smooth' })}
          />
        </>
      )}
    </div>
  );
}

export const FlightTabsSkeleton = () => {
  return (
    <div>
      <ul css={styles.tabs}>
        <li key="firstTab" css={[styles.tabCommon, styles.tab(true)]}>
          <Skeleton width={40} height={10} baseColor={theme.colors.gray80} />
        </li>
        <li key="secondTab" css={[styles.tabCommon, styles.tabActive]}>
          <Skeleton width={40} height={10} baseColor={theme.colors.gray90} />
        </li>
        <li key="thirdTab" css={[styles.tabCommon, styles.tab(true)]}>
          <Skeleton width={40} height={10} baseColor={theme.colors.gray80} />
        </li>
      </ul>
      <div css={styles.bar(false)}>
        <Skeleton width={100} height={16} baseColor={theme.colors.gray90} />
      </div>
    </div>
  );
};
